import type { KeyboardEvent, ReactNode, TransitionEvent } from "react";

import { styles } from "@repo/helpers";

import { usePanelFocus } from "./hooks/usePanelFocus";
import { PanelHeader } from "./panelHeader";

const HANDLES_OWN_ESCAPE = '[aria-expanded="true"], [aria-pressed="true"]';

interface SlidePanelProps {
	id: string;
	title: string;
	side: "left" | "right";
	open: boolean;
	closing: boolean;
	/** Shown beside the map whether or not it is open, so it is never hidden and Escape leaves it alone. */
	pinned?: boolean;
	onClose: () => void;
	onClosed: () => void;
	className?: string;
	contentClassName?: string;
	headerClassName?: string;
	closeClassName?: string;
	children: ReactNode;
}

export function SlidePanel({
	id,
	title,
	side,
	open,
	closing,
	pinned = false,
	onClose,
	onClosed,
	className,
	contentClassName,
	headerClassName,
	closeClassName,
	children,
}: SlidePanelProps) {
	const panelRef = usePanelFocus<HTMLElement>(open);
	const titleId = `${id}-title`;

	const closeOnEscape = (event: KeyboardEvent<HTMLElement>) => {
		const target = event.target as HTMLElement;
		if (event.key !== "Escape" || !open || pinned) return;
		// An open dropdown or a keyboard drag handles its own Escape; portaled content bubbles here through React.
		if (target.matches(HANDLES_OWN_ESCAPE) || !event.currentTarget.contains(target)) return;
		onClose();
	};

	const endClosing = (event: TransitionEvent<HTMLElement>) => {
		if (closing && event.target === event.currentTarget) onClosed();
	};

	return (
		<aside
			id={id}
			ref={panelRef}
			tabIndex={-1}
			aria-labelledby={titleId}
			// Hiding alone doesn't stop Tab: react-select forces its inputs visible.
			{...(!open && !pinned ? { inert: "" } : {})}
			onKeyDown={closeOnEscape}
			onTransitionEnd={endClosing}
			className={styles(
				"absolute inset-y-0 z-30 flex flex-col bg-background shadow-xl outline-none duration-300 ease-out motion-reduce:transition-none",
				side === "left" ? "left-0" : "right-0 border-l border-border",
				open ? "visible translate-x-0" : "invisible",
				!open && side === "left" ? "-translate-x-full" : "",
				!open && side === "right" ? "translate-x-full" : "",
				// Visibility flips at once on open so focus can move in, but waits out the slide on close.
				open
					? "transition-[transform,width]"
					: closing
						? "transition-[transform,visibility,width]"
						: "transition-none",
				className,
			)}
		>
			<div className={styles("flex min-h-0 flex-1 flex-col", contentClassName)}>
				<PanelHeader
					title={title}
					titleId={titleId}
					onClose={onClose}
					className={headerClassName}
					closeClassName={closeClassName}
				/>
				{children}
			</div>
		</aside>
	);
}
